import React, { useEffect, useState } from "react";
import styles from "./EnvoyerPourSignatureModal.module.scss";

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * LS-702 — envoie en signature (Youtrust) le document déjà généré/persisté pour
 * cette ligne de devis. Préremplit prénom/nom/email depuis le contact client
 * (ClientResource), tous les champs restant éditables.
 */
const EnvoyerPourSignatureModal = ({ isOpen, client, onSubmit, onClose }) => {
  const [prenom, setPrenom] = useState("");
  const [nom, setNom] = useState("");
  const [email, setEmail] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    setPrenom(client?.firstname || "");
    setNom(client?.lastname || "");
    setEmail(client?.email || "");
  }, [isOpen, client]);

  if (!isOpen) return null;

  const emailOk = email.trim() !== "" && EMAIL_REGEX.test(email.trim());
  const emailInvalide = email.trim() !== "" && !emailOk;
  const isValid = prenom.trim() !== "" && nom.trim() !== "" && emailOk;

  const busy = isSubmitting;

  const handleCancel = () => {
    if (busy) return;
    onClose();
  };

  const handleKeyDown = (e) => {
    if (e.key === "Escape") handleCancel();
  };

  const handleSubmit = async () => {
    if (!isValid || busy) return;
    setIsSubmitting(true);
    const ok = await onSubmit({
      prenom: prenom.trim(),
      nom: nom.trim(),
      email: email.trim(),
    });
    setIsSubmitting(false);
    if (ok) onClose();
  };

  const manque = [];
  if (prenom.trim() === "") manque.push("un prénom");
  if (nom.trim() === "") manque.push("un nom");
  if (!emailOk) manque.push("un email valide");

  return (
    <div
      className={`modal d-block ${styles.backdrop}`}
      tabIndex="-1"
      role="dialog"
      aria-modal="true"
      aria-labelledby="envoyer-signature-title"
      onClick={handleCancel}
      onKeyDown={handleKeyDown}
    >
      <div
        className="modal-dialog modal-dialog-centered"
        onClick={(e) => e.stopPropagation()}
      >
        <div className={`modal-content ${styles.content}`}>
          <div className={styles.header}>
            <div className={styles.headerIcon} aria-hidden="true">
              <i className="fa fa-file-signature" />
            </div>
            <div className={styles.headerText}>
              <h5 className="modal-title" id="envoyer-signature-title">
                Envoyer pour signature
              </h5>
              <small>
                Le document déjà généré sera envoyé au signataire via Youtrust.
              </small>
            </div>
            <button
              type="button"
              className={styles.closeBtn}
              onClick={handleCancel}
              aria-label="Fermer"
            >
              <span aria-hidden="true">&times;</span>
            </button>
          </div>

          <div className={styles.body}>
            <div className={styles.field}>
              <label htmlFor="envoyer-signature-prenom">Prénom</label>
              <input
                id="envoyer-signature-prenom"
                type="text"
                className="form-control"
                value={prenom}
                onChange={(e) => setPrenom(e.target.value)}
                disabled={busy}
                autoFocus
              />
            </div>

            <div className={styles.field}>
              <label htmlFor="envoyer-signature-nom">Nom</label>
              <input
                id="envoyer-signature-nom"
                type="text"
                className="form-control"
                value={nom}
                onChange={(e) => setNom(e.target.value)}
                disabled={busy}
              />
            </div>

            <div
              className={`${styles.field} ${
                emailInvalide ? styles.fieldError : ""
              }`}
            >
              <label htmlFor="envoyer-signature-email">Email</label>
              <input
                id="envoyer-signature-email"
                type="email"
                className="form-control"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="signataire@exemple.fr"
                disabled={busy}
              />
              {emailInvalide && (
                <p className={styles.errorText}>
                  Cette adresse email n'est pas valide.
                </p>
              )}
            </div>
          </div>

          <div className={styles.footer}>
            <p className={styles.footerHint}>
              {manque.length > 0
                ? `Pour envoyer, renseignez ${manque.join(", ")}.`
                : ""}
            </p>
            <button
              type="button"
              className={styles.btnCancel}
              onClick={handleCancel}
              disabled={busy}
            >
              Annuler
            </button>
            <button
              type="button"
              className={styles.btnSend}
              onClick={handleSubmit}
              disabled={busy || !isValid}
            >
              <i
                className={`fa fa-${busy ? "spinner fa-spin" : "paper-plane"}`}
                aria-hidden="true"
              />
              {busy ? "Envoi..." : "Envoyer"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EnvoyerPourSignatureModal;
